import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { HelpCircle, MessageSquarePlus } from 'lucide-react';
import { useSidebar } from '@/components/chrome/Sidebar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useFlag } from '@/hooks/useFlags';
import { useAuth } from '@/contexts/auth-context';
import { useFeedbackQueue } from './outbox';
import { openFeedbackQueue, openFeedbackSheet } from './state';
import { SUPPORT_EMAIL } from '@/config/brand';

const row = 'flex w-full items-center rounded-md px-2 py-2 text-left text-[13px] font-medium leading-tight text-white/75 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

/**
 * Where "Send feedback" lives, behind the `feedback` flag, for every role (no floating button): on a desktop, a "Help & Tips" row at the foot
 * of the sidebar whose menu opens upward (Help & Tips for the owner, who has that page, Contact support, then Send feedback); on a phone, the
 * same row in the More drawer as a plain "Send feedback" row under the Help item. Contact support and the rate-limit message both use SUPPORT_EMAIL.
 */
export function FeedbackSidebarEntry({ tipsHref }: { /** The Help & Tips page, for the roles that have one (the owner). */ tipsHref?: string }) {
  const { t } = useTranslation('feedback');
  const flag = useFlag('feedback');
  const { impersonation } = useAuth();
  const on = flag && !impersonation; // a platform admin looking at a customer's account sends no note as them
  const { collapsed, onCloseDrawer } = useSidebar();
  const queue = useFeedbackQueue();
  if (!on) return null;
  const label = tipsHref ? t('entry.helpTips') : t('entry.help');
  return (
    <div className="space-y-1 border-t border-white/10 px-2 py-2" data-testid="feedback-entry">
      {/* desktop: the Help & Tips menu, opening upward */}
      <div className="hidden lg:block">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={row} aria-label={collapsed ? label : undefined} title={collapsed ? label : undefined}>
              <HelpCircle className={collapsed ? 'mx-auto h-5 w-5' : 'mr-3 h-5 w-5 shrink-0'} aria-hidden />
              {!collapsed && <span className="flex-1 truncate">{label}</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-52">
            {tipsHref && (
              <DropdownMenuItem asChild>
                <Link href={tipsHref}>{t('entry.helpTips')}</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <a href={`mailto:${SUPPORT_EMAIL}`}>{t('entry.contactSupport')}</a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => openFeedbackSheet()}>{t('entry.sendFeedback')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* phone: the More drawer */}
      <button type="button" className={`${row} lg:hidden`} onClick={() => { onCloseDrawer?.(); openFeedbackSheet(); }}>
        <MessageSquarePlus className="mr-3 h-5 w-5 shrink-0" aria-hidden />
        <span className="flex-1 truncate">{t('entry.sendFeedback')}</span>
      </button>
      {queue.length > 0 && !collapsed && (
        <button type="button" className="flex w-full items-center justify-between gap-2 rounded-md bg-warn-soft px-2 py-1.5 text-left text-xs text-warn-text" onClick={() => { onCloseDrawer?.(); openFeedbackQueue(); }}>
          <span>{t('entry.waiting', { count: queue.length })}</span>
          <span className="font-semibold underline">{t('entry.view')}</span>
        </button>
      )}
    </div>
  );
}
